// utils/dom.ts

// Cache to store CSS content for reuse
const cssCache: Record<string, string> = {};

/**
 * Attaches a list of CSS link elements or inline styles from `window.serverData` to either the document head or a shadow root.
 * If attaching to a shadow root, it will embed the CSS directly to avoid MIME type or CORS issues.
 * Returns a Promise that resolves when all stylesheets have been successfully attached, or rejects if an error occurs.
 *
 * @param {HTMLElement|ShadowRoot} target - The target where the stylesheets will be attached.
 *        Can be either `document.head` or a `ShadowRoot`.
 * @returns {Promise<void>} - A Promise that resolves when all stylesheets are attached, or rejects if an error occurs.
 * @throws {Error} If fetching or attaching a stylesheet fails.
 *
 * @example
 * attachServerStylesheets(shadowRoot)
 *   .then(() => {
 *     // Stylesheets are attached, proceed with rendering
 *     renderComponent();
 *   })
 *   .catch((error) => {
 *     console.error('Failed to attach stylesheets:', error);
 *   });
 */
export async function attachServerStylesheets(target: HTMLElement | ShadowRoot): Promise<void> {
	return new Promise(async (resolve, reject) => {
		if (
			!window.serverData ||
			!window.serverData.stylesheetLinks ||
			window.serverData.stylesheetLinks.length === 0
		) {
			console.warn('No stylesheet links found in serverData.');
			resolve(); // Resolve the promise since there's nothing to attach
			return;
		}

		const stylesheetLinks = window.serverData.stylesheetLinks;

		for (const linkHtml of stylesheetLinks) {
			// Create a temporary element to parse the link HTML
			const tempElement = document.createElement('div');
			tempElement.innerHTML = linkHtml.trim();
			const linkElement = tempElement.firstChild as HTMLLinkElement;

			if (!(linkElement instanceof HTMLLinkElement)) {
				console.warn('Provided link is not a valid HTML <link> element:', linkHtml);
				continue;
			}

			const linkHref = linkElement.href;

			if (target instanceof ShadowRoot) {
				// Check if the CSS content is already cached
				if (cssCache[linkHref]) {
					// Use cached CSS content
					const styleElement = document.createElement('style');
					styleElement.textContent = cssCache[linkHref];
					target.appendChild(styleElement);
				} else {
					// Fetch the CSS content and cache it
					try {
						const response = await fetch(linkHref);
						if (response.ok) {
							const cssContent = await response.text();
							// Cache the fetched CSS content
							cssCache[linkHref] = cssContent;
							const styleElement = document.createElement('style');
							styleElement.textContent = cssContent;
							target.appendChild(styleElement);
						} else {
							console.error(`Failed to fetch CSS from ${linkHref}:`, response.status);
							reject(new Error(`Failed to fetch CSS from ${linkHref}`)); // Reject the promise on fetch failure
							return;
						}
					} catch (error) {
						console.error(`Error fetching CSS from ${linkHref}:`, error);
						reject(error); // Reject the promise on error
						return;
					}
				}
			} else {
				// For document head, append the link element directly
				if (!document.querySelector(`link[href="${linkHref}"]`)) {
					document.head.appendChild(linkElement.cloneNode(true));
				}
			}
		}
		resolve();
	});
}

/**
 * Adds a class to an element if it doesn't already have it.
 * @param {HTMLElement} element - The DOM element to add the class to.
 * @param {string} className - The class name to add.
 */
export function addClass(element: HTMLElement, className: string): void {
	if (!element.classList.contains(className)) {
		element.classList.add(className);
	}
}

/**
 * Removes a class from an element if it exists.
 * @param {HTMLElement} element - The DOM element to remove the class from.
 * @param {string} className - The class name to remove.
 */
export function removeClass(element: HTMLElement, className: string): void {
	if (element.classList.contains(className)) {
		element.classList.remove(className);
	}
}

/**
 * Toggles the visibility of an element.
 * @param {HTMLElement} element - The DOM element to toggle.
 */
export function toggleVisibility(element: HTMLElement): void {
	if (
		element.style.visibility === 'hidden' ||
		getComputedStyle(element).visibility === 'hidden'
	) {
		element.style.visibility = '';
	} else {
		element.style.visibility = 'hidden';
	}
}

/**
 * Injects a script tag into the document.
 *
 * @param {string} src - The source URL of the script to inject.
 * @param {boolean} [async=true] - Whether the script should be loaded asynchronously.
 * @param {boolean} [defer=false] - Whether the script should be deferred.
 * @param {Function} [callback] - A callback function to execute once the script is loaded.
 *
 * @example
 * // Inject a script asynchronously without a callback
 * injectScript('https://example.com/some-script.js');
 *
 * @example
 * // Inject a script with a callback function to execute after the script is loaded
 * injectScript('https://example.com/another-script.js', true, false, () => {
 *     console.log('Script loaded successfully!');
 *     // You can add more code here that depends on the script being loaded.
 * });
 *
 * @example
 * // Inject a script with both async and defer set to true
 * injectScript('https://example.com/yet-another-script.js', true, true);
 */
export function injectScript(
	src: string,
	async: boolean = true,
	defer: boolean = false,
	callback?: () => void,
): void {
	const script = document.createElement('script');
	script.src = src;
	script.async = async;
	script.defer = defer;

	// Ensure the callback is assigned before appending the script
	if (callback) {
		script.onload = () => {
			try {
				callback();
			} catch (error) {
				console.error('Error in callback:', error);
			}
		};
		script.onerror = (error) => {
			console.error('Error loading script:', error);
		};
	}

	// Append the script after setting up the callback and error handling
	document.head.appendChild(script);
}

/**
 * Sets the base font size default CSS variable (--base-font-size-default) based on the computed root font size.
 * This is used to ensure consistent typography scaling and improve responsive design flexibility.
 */
export function setBaseFontSize(): void {
	const rootElement: HTMLElement = document.documentElement;
	const computedFontSize: string = getComputedStyle(rootElement).fontSize;
	const baseFontSizeDefault: number = parseFloat(computedFontSize);

	// Create a style tag and add the CSS variable
	let styleTag: HTMLStyleElement | null = document.querySelector('#font-size-base-default-style');

	if (!styleTag) {
		styleTag = document.createElement('style');
		styleTag.id = 'font-size-base-default-style';
		document.head.appendChild(styleTag);
	}

	// Set the CSS variable in the style tag
	styleTag.textContent = `:root { --font-size-base-default: ${baseFontSizeDefault}px; }`;
}

/**
 * Retrieves the current base font size as set in the CSS variable (--font-size-base-default).
 * This function uses getComputedStyle to fetch the value, as it's set in a style tag.
 *
 * @returns {string | null} - The current base font size in pixels or null if not set.
 */
export function getBaseFontSize(): string | null {
	const computedStyle = getComputedStyle(document.documentElement);
	return computedStyle.getPropertyValue('--font-size-base-default') || null;
}
